import { Link } from 'react-router';
import type { EnrolledCourse } from '@lp/shared';
import { ArrowRight, BookOpen, Layers } from 'lucide-react';
import { Badge, Card, ProgressRing, StatusBadge } from '../ui.js';
import { formatDuration, pluralize } from '../../lib/format.js';

/**
 * A deterministic cover for a course with no thumbnail.
 *
 * The palette is four semantic washes, chosen by a hash of the slug so a
 * course keeps the same cover between visits, and every one of them carries
 * both themes through the tokens.
 */
const COVERS = [
  'bg-project-wash text-project',
  'bg-surface-3 text-ink-muted',
  'bg-ink text-canvas',
  'bg-surface-2 text-ink-muted',
] as const;

function coverFor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  return COVERS[hash % COVERS.length] ?? COVERS[0];
}

function monogram(title: string): string {
  return (
    title
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  );
}

export function CourseCard({ course }: { course: EnrolledCourse }) {
  const href = `/courses/${course.slug}`;
  const isDraft = course.status !== 'published';
  const { resume, progress } = course;
  const resumeHref = resume
    ? `/courses/${course.slug}/sessions/${resume.sessionSlug}${
        resume.positionSec > 0 ? `?t=${Math.floor(resume.positionSec)}` : ''
      }`
    : null;

  return (
    <Card as="li" className="flex flex-col overflow-hidden">
      {course.thumbnailUrl ? (
        <img
          src={course.thumbnailUrl}
          alt=""
          className="h-32 w-full object-cover border-b border-line"
          loading="lazy"
        />
      ) : (
        <div
          aria-hidden
          className={`h-28 w-full border-b border-line grid place-items-center ${coverFor(
            course.slug,
          )}`}
        >
          <span className="font-mono text-2xl font-semibold tracking-[0.12em]">
            {monogram(course.title)}
          </span>
        </div>
      )}

      <div className="flex-1 flex flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {isDraft ? (
              <div className="mb-1.5">
                <StatusBadge status={course.status} />
              </div>
            ) : null}
            <h3 className="font-semibold text-ink leading-tight">
              <Link to={href} className="hover:underline underline-offset-2">
                {course.title}
              </Link>
            </h3>
          </div>
          <ProgressRing
            value={progress.pct}
            label={`${Math.round(progress.pct)}% of ${course.title} complete`}
          />
        </div>

        {course.summary ? (
          <p className="text-sm text-ink-muted leading-relaxed line-clamp-3">{course.summary}</p>
        ) : null}

        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-muted mt-auto pt-1">
          <li className="inline-flex items-center gap-1.5">
            <Layers size={13} aria-hidden />
            {pluralize(course.sessionCount, 'session')}
          </li>
          <li className="inline-flex items-center gap-1.5">
            <BookOpen size={13} aria-hidden />
            {progress.completedSessions} of {progress.totalSessions} done
          </li>
          {course.hasCapstone ? (
            <li>
              <Badge tone="project">Capstone</Badge>
            </li>
          ) : null}
        </ul>

        <div className="pt-2">
          {resumeHref && resume ? (
            <Link
              to={resumeHref}
              className="inline-flex items-center justify-center gap-2 w-full rounded-control border border-brand bg-brand px-3.5 py-2 text-sm font-semibold text-brand-ink hover:bg-brand-strong hover:border-brand-strong transition-colors duration-150 ease-out"
            >
              Continue where you left off
              <ArrowRight size={15} aria-hidden />
            </Link>
          ) : (
            <Link
              to={href}
              className="inline-flex items-center justify-center gap-2 w-full rounded-control border border-brand bg-brand px-3.5 py-2 text-sm font-semibold text-brand-ink hover:bg-brand-strong hover:border-brand-strong transition-colors duration-150 ease-out"
            >
              {progress.completedSessions > 0 ? 'Open the course' : 'Start the course'}
              <ArrowRight size={15} aria-hidden />
            </Link>
          )}
          {resume ? (
            <p className="text-xs text-ink-muted mt-2 truncate">
              Session {resume.orderIndex + 1} · {resume.sessionTitle}
              {resume.positionSec > 0 ? ` · paused at ${formatDuration(resume.positionSec)}` : ''}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
